import React, { useState, useEffect } from 'react';
import {
  ArrowRight,
  CheckCircle,
  Brain,
  Calendar,
  FileText,
  Users,
  TrendingUp,
  Sparkles,
  Clock,
  Target,
  Award,
  ChevronDown,
  Play,
  Zap,
  BarChart,
  DollarSign
} from 'lucide-react';
import sheppardProfile, { animationTimings } from './mockData/showcaseMockData';

// Import demo sections
import HeroSectionNew from './sections/HeroSectionNew';
import AssessmentDemoActual from './sections/AssessmentDemoActual';
import ProfileToBragSheetFlow from './sections/ProfileToBragSheetFlowUpdated';
import CollegeCategorizationFlow from './sections/CollegeCategorizationWithED';
import SmartTimelineDemo from './sections/SmartSchedulingCompact';
import FreewritingDemo from './sections/FreewritingDemo';
import IdeasDemo from './sections/IdeasDemoCompact';
import EssayDevelopmentDemo from './sections/EssayDevelopmentDemo';
import RecommendationDemo from './sections/RecommendationsAssistantNew';
import ScholarshipsDemo from './sections/ScholarshipsDemo';
import InsightsDemo from './sections/InsightsDemo';
import ResultsSection from './sections/ResultsSection';

const CollegeJourneyShowcase = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const [sectionAnimationComplete, setSectionAnimationComplete] = useState({});

  // Helper to mark section animation as complete
  const markSectionComplete = (sectionId) => {
    setSectionAnimationComplete(prev => ({ ...prev, [sectionId]: true }));
  };

  // Simplified scroll detection (from Aspirational)
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      const sectionElements = sections.map(s => document.getElementById(s.id));

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const element = sectionElements[i];
        if (element && element.offsetTop <= scrollPosition) {
          setCurrentSection(i);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Progress indicator dots
  const sections = [
    { id: 'hero', name: 'Start', icon: Sparkles },
    { id: 'assessment', name: 'Assessment', icon: Brain },
    { id: 'profile-brag', name: 'Profile→Brag', icon: Award },
    { id: 'college-intel', name: 'College Intel', icon: Target },
    { id: 'timeline', name: 'Timeline', icon: Calendar },
    { id: 'freewriting', name: 'Freewriting', icon: FileText },
    { id: 'ideas', name: 'Ideas', icon: Brain },
    { id: 'essay-dev', name: 'Essay Dev', icon: Zap },
    { id: 'recommendations', name: 'Letters', icon: Users },
    { id: 'scholarships', name: 'Scholarships', icon: DollarSign },
    { id: 'results', name: 'Results', icon: Award }
  ];

  const scrollToSection = (index) => {
    const element = document.getElementById(sections[index].id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Side Navigation Dots (from Aspirational) */}
      {hasStarted && (
        <div className="fixed right-8 top-1/2 -translate-y-1/2 z-50 hidden lg:flex flex-col gap-4">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => scrollToSection(index)}
                className={`group relative flex items-center justify-center w-10 h-10 rounded-full transition-all
                  ${currentSection === index
                    ? 'bg-azure-600 shadow-lg scale-110'
                    : 'bg-white shadow-md hover:shadow-lg hover:scale-105'}`}
              >
                <Icon className={`w-5 h-5 ${currentSection === index ? 'text-white' : 'text-slate-600'}`} />

                {/* Tooltip */}
                <div className="absolute right-14 px-2 py-1 bg-slate-900 text-white text-xs rounded
                  opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                  {section.name}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Sections */}
      <section id="hero" className="min-h-[600px] lg:min-h-screen flex items-center justify-center py-6 lg:py-16">
        <HeroSectionNew
          onStart={() => {
            setHasStarted(true);
            scrollToSection(1);
          }}
          onAnimationComplete={() => markSectionComplete('hero')}
          onContinue={() => scrollToSection(1)}
        />
      </section>

      <section id="assessment" className="min-h-[650px] lg:min-h-screen flex items-center justify-center py-6 lg:py-16">
        <AssessmentDemoActual
          data={sheppardProfile}
          onAnimationComplete={() => markSectionComplete('assessment')}
          onContinue={() => scrollToSection(2)}
        />
      </section>

      <section id="profile-brag" className="min-h-[650px] lg:min-h-screen flex items-center justify-center py-8 lg:py-16">
        <ProfileToBragSheetFlow
          data={sheppardProfile}
          onAnimationComplete={() => markSectionComplete('profile-brag')}
          onContinue={() => scrollToSection(3)}
        />
      </section>

      <section id="college-intel" className="min-h-[600px] lg:min-h-screen flex items-center justify-center py-8 lg:py-16">
        <CollegeCategorizationFlow
          data={sheppardProfile}
          onAnimationComplete={() => markSectionComplete('college-intel')}
          onContinue={() => scrollToSection(4)}
        />
      </section>

      <section id="timeline" className="min-h-[550px] lg:min-h-screen flex items-center justify-center py-8 lg:py-16">
        <SmartTimelineDemo
          data={sheppardProfile}
          onAnimationComplete={() => markSectionComplete('timeline')}
          onContinue={() => scrollToSection(5)}
        />
      </section>

      <section id="freewriting" className="min-h-[500px] lg:min-h-screen flex items-center justify-center py-8 lg:py-16">
        <FreewritingDemo
          data={sheppardProfile}
          onAnimationComplete={() => markSectionComplete('freewriting')}
          onContinue={() => scrollToSection(6)}
        />
      </section>

      <section id="ideas" className="min-h-[500px] lg:min-h-screen flex items-center justify-center py-8 lg:py-16">
        <IdeasDemo
          data={sheppardProfile}
          onAnimationComplete={() => markSectionComplete('ideas')}
          onContinue={() => scrollToSection(7)}
        />
      </section>

      <section id="essay-dev" className="min-h-[600px] lg:min-h-screen flex items-center justify-center py-8 lg:py-16">
        <EssayDevelopmentDemo
          data={sheppardProfile}
          onAnimationComplete={() => markSectionComplete('essay-dev')}
          onContinue={() => scrollToSection(8)}
        />
      </section>

      <section id="recommendations" className="min-h-[350px] lg:min-h-screen flex items-center justify-center py-3 lg:py-16">
        <RecommendationDemo
          data={sheppardProfile}
          onAnimationComplete={() => markSectionComplete('recommendations')}
          onContinue={() => scrollToSection(9)}
        />
      </section>

      <section id="scholarships" className="min-h-[320px] lg:min-h-screen flex items-center justify-center py-3 lg:py-16">
        <ScholarshipsDemo
          data={sheppardProfile}
          onAnimationComplete={() => markSectionComplete('scholarships')}
          onContinue={() => scrollToSection(10)}
        />
      </section>

      <section id="results" className="min-h-[320px] lg:min-h-screen flex items-center justify-center py-3 lg:py-16 pb-8 lg:pb-40">
        <ResultsSection
          data={sheppardProfile}
          onAnimationComplete={() => markSectionComplete('results')}
          onContinue={() => {
            // Navigate to sign-up or main app
            window.location.href = '/';
          }}
          isLastSection={true}
        />
      </section>

    </div>
  );
};

export default CollegeJourneyShowcase;